/**
 * 🚀 NANO-METADATA UPLOAD HOOK
 *
 * Ultra-fast file metadata extraction with TWO PHASES:
 *
 * PHASE 1 (INSTANT - 0ms): Client-side extraction
 *   - File name, size, extension, mime type
 *   - File type detection (PDF, Excel, Word, etc.)
 *   - Health score estimate
 *   - Context type guess
 *   - NO network call required!
 *
 * PHASE 2 (Fast - 0.3-0.8s): Server enhancement
 *   - Deep metadata (page count, sheet names, etc.)
 *   - OCR requirement detection
 *   - Health score refinement
 *
 * ✅ FIXED: Uses apiClient for proper auth handling
 * ✅ FIXED: Better error handling with retry support
 * ✅ NEW: Instant client-side metadata (Phase 1)
 *
 * @module useNanoMetadataUpload
 */

import { useState, useCallback } from 'react';

import apiClient from '../services/apiClient';

// ═══════════════════════════════════════════════════════════════════════════
// CLIENT-SIDE FILE TYPE DETECTION (INSTANT - NO NETWORK!)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Detect file type from extension (client-side, instant)
 */
const detectFileTypeClient = (extension, mimeType) => {
  const ext = extension?.toLowerCase() || '';

  // File type mappings
  const typeMap = {
    // Documents
    pdf: {
      baseType: 'pdf',
      category: 'document',
      ocrRequired: false,
      contextGuess: 'document',
      hints: ['PDF document'],
    },
    doc: {
      baseType: 'word',
      category: 'document',
      ocrRequired: false,
      contextGuess: 'document',
      hints: ['Word document'],
    },
    docx: {
      baseType: 'word',
      category: 'document',
      ocrRequired: false,
      contextGuess: 'document',
      hints: ['Word document'],
    },

    // Spreadsheets
    xls: {
      baseType: 'excel',
      category: 'spreadsheet',
      ocrRequired: false,
      contextGuess: 'financial',
      hints: ['Excel spreadsheet'],
    },
    xlsx: {
      baseType: 'excel',
      category: 'spreadsheet',
      ocrRequired: false,
      contextGuess: 'financial',
      hints: ['Excel spreadsheet'],
    },
    csv: {
      baseType: 'csv',
      category: 'spreadsheet',
      ocrRequired: false,
      contextGuess: 'data',
      hints: ['CSV data file'],
    },

    // Presentations
    ppt: {
      baseType: 'powerpoint',
      category: 'presentation',
      ocrRequired: false,
      contextGuess: 'presentation',
      hints: ['PowerPoint'],
    },
    pptx: {
      baseType: 'powerpoint',
      category: 'presentation',
      ocrRequired: false,
      contextGuess: 'presentation',
      hints: ['PowerPoint'],
    },

    // Images (OCR required)
    jpg: {
      baseType: 'image',
      category: 'image',
      ocrRequired: true,
      contextGuess: 'scanned',
      hints: ['Image - OCR needed'],
    },
    jpeg: {
      baseType: 'image',
      category: 'image',
      ocrRequired: true,
      contextGuess: 'scanned',
      hints: ['Image - OCR needed'],
    },
    png: {
      baseType: 'image',
      category: 'image',
      ocrRequired: true,
      contextGuess: 'scanned',
      hints: ['Image - OCR needed'],
    },
    tiff: {
      baseType: 'image',
      category: 'image',
      ocrRequired: true,
      contextGuess: 'scanned',
      hints: ['Image - OCR needed'],
    },
    tif: {
      baseType: 'image',
      category: 'image',
      ocrRequired: true,
      contextGuess: 'scanned',
      hints: ['Image - OCR needed'],
    },

    // Text
    txt: {
      baseType: 'text',
      category: 'text',
      ocrRequired: false,
      contextGuess: 'text',
      hints: ['Plain text'],
    },
    md: {
      baseType: 'text',
      category: 'text',
      ocrRequired: false,
      contextGuess: 'text',
      hints: ['Markdown'],
    },

    // Audio/Video
    mp3: {
      baseType: 'audio',
      category: 'media',
      ocrRequired: false,
      contextGuess: 'audio',
      hints: ['Audio - transcription needed'],
    },
    wav: {
      baseType: 'audio',
      category: 'media',
      ocrRequired: false,
      contextGuess: 'audio',
      hints: ['Audio - transcription needed'],
    },
    mp4: {
      baseType: 'video',
      category: 'media',
      ocrRequired: false,
      contextGuess: 'video',
      hints: ['Video - transcription needed'],
    },
  };

  const info = typeMap[ext] || {
    baseType: 'unknown',
    category: 'other',
    ocrRequired: false,
    contextGuess: 'unknown',
    hints: ['Unknown file type'],
  };

  // Estimate pages based on file size
  const sizeKB = 0; // Will be set in caller
  info.pageEstimate = null;

  return info;
};

/**
 * Estimate health score based on file characteristics (client-side, instant)
 */
const estimateHealthClient = (file, typeInfo) => {
  const sizeKB = file.size / 1024;
  const sizeMB = sizeKB / 1024;

  let score = 85; // Base score
  let insight = '';

  // Size-based scoring
  if (sizeMB > 100) {
    score -= 20;
    insight = `Large file (${sizeMB.toFixed(1)}MB) - may take longer to process`;
  } else if (sizeMB > 50) {
    score -= 10;
    insight = `Medium-large file (${sizeMB.toFixed(1)}MB)`;
  } else if (sizeMB < 0.01) {
    score -= 15;
    insight = 'Very small file - may have limited content';
  } else {
    insight = `${typeInfo.category.charAt(0).toUpperCase() + typeInfo.category.slice(1)} file ready for analysis`;
  }

  // Type-based adjustments
  if (typeInfo.ocrRequired) {
    score -= 10;
    insight = `Image file - OCR will be applied`;
  }

  if (typeInfo.baseType === 'excel' || typeInfo.baseType === 'csv') {
    score += 5;
    insight = `Spreadsheet detected - numeric analysis available`;
  }

  if (typeInfo.baseType === 'pdf') {
    // Estimate pages: ~50KB per page for text PDFs, ~200KB for scanned
    const estimatedPages = Math.max(1, Math.round(sizeKB / 75));
    insight = `~${estimatedPages} page${estimatedPages !== 1 ? 's' : ''} estimated`;
  }

  return {
    score: Math.max(10, Math.min(100, score)),
    insight,
  };
};

/**
 * Hook for nano-metadata upload with streaming
 * @returns {Object} { rows, isUploading, uploadFile, uploadFiles, clearRows, error, retryFile }
 */
export function useNanoMetadataUpload(options = {}) {
  const {
    apiNamespace = 'smart-notebook', // 'smart-notebook' | 'intelligence-studio'
    serverEnhancement = true, // If false: Phase-1 is client-only (no duplicate upload)
  } = options;
  const [rows, setRows] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Upload a single file and stream metadata into rows
   * ✅ FIX: Show INSTANT client-side metadata first, then enhance with server data
   */
  const uploadFile = useCallback(
    async (file, desiredRowId = null) => {
      setError(null);

      // ═══════════════════════════════════════════════════════════════════════════
      // PHASE 1: INSTANT CLIENT-SIDE METADATA (0ms - NO network call!)
      // ═══════════════════════════════════════════════════════════════════════════
      const tempId =
        typeof desiredRowId === 'string' && desiredRowId.trim().length > 0
          ? desiredRowId
          : `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const extension = file.name.split('.').pop()?.toLowerCase() || '';

      // Detect file type and category INSTANTLY (client-side)
      const fileTypeInfo = detectFileTypeClient(extension, file.type);
      const healthEstimate = estimateHealthClient(file, fileTypeInfo);

      const placeholderRow = {
        file_id: tempId,
        filename: file.name, // ✅ Stable matching key for Phase-2 sync (fallback)
        isLoading: false, // ✅ NOT loading - we have instant data!
        isEnhancing: true, // ✅ Flag: server enhancement in progress
        originalFile: file,
        basic_info: {
          name: file.name,
          extension,
          base_type: fileTypeInfo.baseType,
          category: fileTypeInfo.category,
          size_bytes: file.size,
          size_formatted: formatBytes(file.size),
          created_at: new Date().toISOString(),
        },
        quick_health: {
          // ✅ ENTERPRISE V2: Health is NULL until backend completes processing
          // Client-side estimate is stored separately for reference, NOT as ai_readiness_score
          ai_readiness_score: null, // ← NEVER show client-side estimates!
          client_estimate: healthEstimate.score, // ← For debugging only
          is_corrupted: false,
          is_encrypted: false,
          ocr_required: fileTypeInfo.ocrRequired,
          is_pending: true, // ← Explicit pending flag
        },
        context_type_guess: {
          type: fileTypeInfo.contextGuess,
          confidence: 0.7,
        },
        details: {
          micro_insight: healthEstimate.insight,
          page_count_estimate: fileTypeInfo.pageEstimate,
        },
        processing_status: {
          ocr: fileTypeInfo.ocrRequired ? 'pending' : 'not_needed',
          vectorization: 'pending',
        },
        processing_hints: fileTypeInfo.hints,
      };

      // ✅ DEDUP: Check if file already exists by name before adding
      setRows(prev => {
        const existingByName = prev.find(
          r => (r.basic_info?.name || r.originalFile?.name) === file.name
        );
        if (existingByName && !existingByName.hasError) {
          // If existing is still a placeholder/low-signal row, UPGRADE it (enterprise: no stale placeholders)
          const isPlaceholder =
            existingByName.isLoading === true ||
            existingByName.isEnhancing === true ||
            existingByName.quick_health?.ai_readiness_score === 0 ||
            String(existingByName.details?.micro_insight || '')
              .toLowerCase()
              .includes('analyzing');

          if (!isPlaceholder) {
            console.log(`⚠️ [NanoMetadata] Skipping duplicate: ${file.name}`);
            return prev; // Don't add duplicate
          }
        }
        // Remove any existing error entry for this file (for retry)
        const filtered = prev.filter(
          r => (r.basic_info?.name || r.originalFile?.name) !== file.name && r.file_id !== tempId
        );
        return [...filtered, placeholderRow];
      });

      // ✅ LOG: Instant metadata is now available!
      console.log(
        `⚡ [NanoMetadata] INSTANT: ${file.name} | ${fileTypeInfo.baseType} | ${healthEstimate.score}% health | ${healthEstimate.insight}`
      );

      // ✅ Intelligence Studio enterprise mode: avoid double-uploading the same file just for nano-metadata
      if (!serverEnhancement) {
        setRows(prev => prev.map(r => (r.file_id === tempId ? { ...r, isEnhancing: false } : r)));
        return placeholderRow;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        // ✅ FIX: Use explicit /api prefix + namespace to ensure correct routing
        const response = await apiClient.post(`/api/${apiNamespace}/nano-metadata`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // ✅ FIX: Handle various response structures from apiClient
        // response could be: { data: { success, data } } OR { success, data } OR { data: metadata }
        const rawData = response?.data || response;

        // Extract metadata from various possible structures
        let metadata = null;

        if (rawData?.success && rawData?.data) {
          // Structure: { success: true, data: metadata }
          metadata = rawData.data;
        } else if (rawData?.data?.success && rawData?.data?.data) {
          // Structure: { data: { success: true, data: metadata } }
          metadata = rawData.data.data;
        } else if (rawData?.file_id || rawData?.basic_info) {
          // Direct metadata object
          metadata = rawData;
        } else if (rawData?.data?.file_id || rawData?.data?.basic_info) {
          // Wrapped metadata object
          metadata = rawData.data;
        }

        if (metadata && (metadata.file_id || metadata.basic_info)) {
          // ═══════════════════════════════════════════════════════════════════════════
          // PHASE 2: MERGE server metadata with client-side data (enhancement)
          // ═══════════════════════════════════════════════════════════════════════════
          setRows(prev =>
            prev.map(row => {
              if (row.file_id !== tempId) return row;

              // Merge: server data overrides client data where available
              return {
                ...row, // Keep client-side instant data as base
                ...metadata, // Server data overrides
                file_id: metadata.file_id || row.file_id,
                isLoading: false,
                isEnhancing: false, // ✅ Enhancement complete
                originalFile: file,
                // Merge nested objects properly
                basic_info: { ...row.basic_info, ...metadata.basic_info },
                quick_health: { ...row.quick_health, ...metadata.quick_health },
                details: { ...row.details, ...metadata.details },
                processing_status: { ...row.processing_status, ...metadata.processing_status },
              };
            })
          );
          console.log(`✅ [NanoMetadata] Enhanced "${file.name}" with server data`);
          return metadata;
        } else {
          // Server didn't return metadata, but we still have client-side data!
          console.warn('[NanoMetadata] No server metadata, using client-side only:', file.name);
          setRows(prev =>
            prev.map(row =>
              row.file_id === tempId
                ? { ...row, isEnhancing: false } // Mark enhancement as done (no server data)
                : row
            )
          );
          return placeholderRow; // Return the client-side data
        }
      } catch (err) {
        console.error('❌ [NanoMetadata] Server enhancement failed:', err);
        const errorMessage = err.response?.data?.error || err.message || 'Enhancement failed';

        // ✅ FIX: Keep client-side data, just mark enhancement as failed
        // User still sees instant metadata, just without deep server analysis
        setRows(prev =>
          prev.map(row =>
            row.file_id === tempId
              ? {
                  ...row,
                  isLoading: false,
                  isEnhancing: false,
                  enhancementFailed: true, // Flag that server enhancement failed
                  // Keep the client-side data, just update the insight
                  details: {
                    ...row.details,
                    micro_insight: row.details.micro_insight + ' (server analysis pending)',
                  },
                }
              : row
          )
        );

        return placeholderRow; // Still return client-side data
      }
    },
    [apiNamespace, serverEnhancement]
  );

  /**
   * Retry a failed file
   */
  const retryFile = useCallback(
    async fileId => {
      const row = rows.find(r => r.file_id === fileId);
      if (!row?.originalFile) {
        console.error('❌ [NanoMetadata] No original file found for retry');
        return null;
      }

      // Remove the failed row first
      setRows(prev => prev.filter(r => r.file_id !== fileId));

      // Re-upload the file
      return uploadFile(row.originalFile);
    },
    [rows, uploadFile]
  );

  /**
   * Upload multiple files in parallel with streaming
   */
  const uploadFiles = useCallback(
    async files => {
      setIsUploading(true);
      setError(null);

      const results = await Promise.all(Array.from(files).map(file => uploadFile(file)));

      setIsUploading(false);

      return results.filter(Boolean);
    },
    [uploadFile]
  );

  /**
   * Upload files using batch endpoint (faster for many files)
   * NOTE: If placeholders already exist, this will REPLACE them instead of adding new ones
   */
  const uploadFilesBatch = useCallback(
    async files => {
      setIsUploading(true);
      setError(null);

      // ✅ FIX: Check if placeholders already exist (added synchronously before this call)
      // If they exist, we'll replace them. If not, we'll add new ones.
      const existingPlaceholders = rows.filter(r => r.isLoading && r.file_id?.startsWith('temp_'));

      // Only add new placeholders if they don't already exist
      if (existingPlaceholders.length === 0) {
        const placeholders = Array.from(files).map((file, i) => ({
          file_id: `temp_batch_${Date.now()}_${i}`,
          isLoading: true,
          originalFile: file,
          basic_info: {
            name: file.name,
            extension: file.name.split('.').pop()?.toLowerCase() || '',
            size_bytes: file.size,
            size_formatted: formatBytes(file.size),
          },
          quick_health: { ai_readiness_score: null, is_pending: true },
          context_type_guess: null,
          details: { micro_insight: 'Analyzing...' },
          processing_status: { ocr: 'analyzing', vectorization: 'pending' },
        }));

        // ✅ DEDUP: Only add placeholders for files not already in rows
        setRows(prev => {
          const newPlaceholders = placeholders.filter(p => {
            const name = p.basic_info?.name;
            const existingByName = prev.find(
              r => (r.basic_info?.name || r.originalFile?.name) === name
            );
            if (existingByName && !existingByName.hasError) {
              console.log(`⚠️ [NanoMetadata Batch] Skipping duplicate: ${name}`);
              return false;
            }
            return true;
          });
          // Remove any existing error entries for these files (for retry)
          const fileNames = newPlaceholders.map(p => p.basic_info?.name);
          const filtered = prev.filter(r => {
            const rName = r.basic_info?.name || r.originalFile?.name;
            return !fileNames.includes(rName);
          });
          return [...filtered, ...newPlaceholders];
        });
      }

      // ✅ If server enhancement disabled, just create placeholders and return (no network)
      if (!serverEnhancement) {
        const results = await Promise.all(
          Array.from(files).map((f, i) => uploadFile(f, `temp_batch_${Date.now()}_${i}`))
        );
        setIsUploading(false);
        return results.filter(Boolean);
      }

      try {
        const formData = new FormData();
        Array.from(files).forEach(file => {
          formData.append('files', file);
        });

        // ✅ FIX: Use explicit /api prefix + namespace to ensure correct routing
        const response = await apiClient.post(
          `/api/${apiNamespace}/nano-metadata/batch`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        const result = response?.data || response;

        if (result.success && result.data?.files) {
          // Replace placeholders with real data (match by file name if temp IDs don't match)
          setRows(prev => {
            const resultFiles = result.data.files;
            return prev.map(row => {
              // Find matching result by filename (handles both temp_batch_ and temp_ prefixes)
              const matchingResult = resultFiles.find((rf, idx) => {
                const resultName = rf.basic_info?.name || rf.name;
                const rowName = row.basic_info?.name || row.name;
                return (
                  resultName === rowName ||
                  (row.isLoading &&
                    (row.file_id?.startsWith('temp_batch_') || row.file_id?.startsWith('temp_')) &&
                    idx < resultFiles.length)
                );
              });

              if (matchingResult) {
                return {
                  ...matchingResult,
                  isLoading: false,
                  originalFile: files[resultFiles.indexOf(matchingResult)] || row.originalFile,
                };
              }
              return row;
            });
          });

          return result.data.files;
        } else {
          throw new Error(result.error || 'Unknown batch error');
        }
      } catch (err) {
        console.error('❌ [NanoMetadata] Batch upload error:', err);
        const errorMessage = err.response?.data?.error || err.message || 'Batch upload failed';
        setError(errorMessage);

        // Mark all placeholders as error with retry option
        setRows(prev =>
          prev.map(row =>
            row.file_id?.startsWith('temp_batch_')
              ? {
                  ...row,
                  isLoading: false,
                  hasError: true,
                  canRetry: true,
                  errorMessage: errorMessage,
                  details: { ...row.details, micro_insight: `⚠️ ${errorMessage}` },
                }
              : row
          )
        );

        return [];
      } finally {
        setIsUploading(false);
      }
    },
    [apiNamespace, serverEnhancement, rows, uploadFile]
  );

  /**
   * Clear all rows
   */
  const clearRows = useCallback(() => {
    setRows([]);
    setError(null);
  }, []);

  /**
   * Set rows directly (for existing files from server)
   */
  const setRowsDirectly = useCallback(newRows => {
    setRows(newRows || []);
    setError(null);
  }, []);

  /**
   * Add rows (append to existing)
   */
  const addRows = useCallback(newRows => {
    setRows(prev => [...prev, ...(newRows || [])]);
  }, []);

  /**
   * Remove a specific row by file_id
   */
  const removeRow = useCallback(fileId => {
    setRows(prev => prev.filter(row => row.file_id !== fileId));
  }, []);

  /**
   * Get count of files in different states
   */
  const getStats = useCallback(() => {
    return {
      total: rows.length,
      loading: rows.filter(r => r.isLoading).length,
      ready: rows.filter(r => !r.isLoading && !r.hasError).length,
      errors: rows.filter(r => r.hasError).length,
      needsOcr: rows.filter(r => r.quick_health?.ocr_required).length,
    };
  }, [rows]);

  return {
    rows,
    isUploading,
    error,
    uploadFile,
    uploadFiles,
    uploadFilesBatch,
    clearRows,
    setRowsDirectly, // ✅ NEW: Set rows directly (for existing files)
    addRows, // ✅ NEW: Add rows (append)
    removeRow,
    retryFile, // ✅ NEW: Retry failed files
    getStats, // ✅ NEW: Get upload statistics
  };
}

/**
 * Format bytes to human-readable size
 */
function formatBytes(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default useNanoMetadataUpload;
